
import { CardElement } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";

interface CardPaymentFormProps {
  amount: string;
  isProcessing: boolean;
  stripe: any;
  elements: any;
  onSubmit: (paymentMethod: any) => Promise<void>;
}

const CardPaymentForm = ({ amount, isProcessing, stripe, elements, onSubmit }: CardPaymentFormProps) => {
  const [cardError, setCardError] = useState<string | null>(null);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [localProcessing, setLocalProcessing] = useState(false);

  useEffect(() => {
    if (!stripe || !elements) {
      console.log("Stripe.js has not yet loaded.");
    } else {
      console.log("Stripe and Elements are ready for card payment");
    }
  }, [stripe, elements]);

  // Function to get user-friendly card error messages
  const getUserFriendlyCardError = (errorMessage: string) => {
    // Map technical error codes to user-friendly messages
    if (errorMessage.includes("incomplete_number")) {
      return "Please enter a complete card number.";
    } else if (errorMessage.includes("invalid_number")) {
      return "The card number is invalid. Please check and try again.";
    } else if (errorMessage.includes("incomplete_expiry")) {
      return "Please enter the card's expiration date.";
    } else if (errorMessage.includes("invalid_expiry")) {
      return "The expiration date is invalid. Please check and try again.";
    } else if (errorMessage.includes("incomplete_cvc")) {
      return "Please enter the security code (CVC).";
    } else if (errorMessage.includes("invalid_cvc")) {
      return "The security code is invalid. Please check and try again.";
    }
    
    return errorMessage;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      console.log("Stripe.js has not yet loaded.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    
    if (!cardElement) {
      console.log("Card Element not found.");
      return;
    }

    setLocalProcessing(true);
    console.log("Creating payment method...");
    
    try {
      // Validate card before creating payment method
      const { error: validationError } = await stripe.createToken(cardElement);
      if (validationError) {
        console.error("Card validation error:", validationError);
        setCardError(getUserFriendlyCardError(validationError.message) || 'Your card information is invalid. Please check and try again.');
        setLocalProcessing(false);
        return;
      }
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        console.error("Stripe error:", error);
        setCardError(getUserFriendlyCardError(error.message) || 'An error occurred with your payment');
        setLocalProcessing(false);
        return;
      }

      console.log("Payment method created:", paymentMethod);
      setCardError(null);
      
      // Submit payment
      await onSubmit(paymentMethod);
      console.log("Payment submitted successfully");
    } catch (error: any) {
      console.error("Payment submission error:", error);
      setCardError(getUserFriendlyCardError(error.message) || 'An error occurred with your payment');
    } finally {
      setLocalProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="card-element" className="text-base font-semibold block">
          Card Details
        </label>
        <div className={`border rounded-lg p-4 ${cardError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}>
          <CardElement 
            id="card-element"
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: false,
            }}
            onChange={(e) => {
              setIsCardComplete(e.complete);
              if (e.error) {
                setCardError(getUserFriendlyCardError(e.error.message));
              } else {
                setCardError(null);
              }
            }}
          />
        </div>
        {cardError && (
          <p className="text-red-500 text-sm flex items-start mt-1">
            <span className="mr-1">⚠️</span>
            <span>{cardError}</span>
          </p>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:justify-between items-center gap-4">
        <div className="text-center md:text-left">
          <p className="text-sm text-gray-600">Total Amount</p>
          <p className="text-2xl font-bold text-[#007BFF]">${amount}</p>
        </div>
        <Button 
          type="submit" 
          className="bg-[#007BFF] hover:bg-blue-600 text-white w-full md:w-auto"
          disabled={localProcessing || isProcessing || !stripe || !elements || !isCardComplete}
        >
          {localProcessing || isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay $${amount}`
          )}
        </Button>
      </div>
    </form>
  );
};

export default CardPaymentForm;
